import React from 'react';
import { Hash, Mic, FileText, Image as ImageIcon, VolumeX } from 'lucide-react';
import { formatTime, initials, previewText } from '@/utils/commsFormat';
import { MessageSearch, isSearchActive } from './MessageSearch';

const PreviewIcon = ({ type }) => {
  if (type === 'voice') return <Mic size={12} />;
  if (type === 'file') return <FileText size={12} />;
  return null;
};

const preview = (c) => {
  if (!c.last_message_at) {
    if (c.kind === 'team') return 'No messages yet';
    return c.online ? 'Online' : 'Offline';
  }
  const text = previewText({ message_type: c.last_message_type, message: c.last_message, transcript: c.last_message_type === 'voice' ? c.last_message : null, file_info: { filename: c.last_message } });
  return c.kind === 'team' && c.last_message_from ? `${c.last_message_from}: ${text}` : text;
};

const ConversationItem = ({ conversation: c, selected, onClick }) => {
  const unread = c.unread_count > 0;
  const isTeam = c.kind === 'team';
  return (
    <button
      type="button"
      className={`contact-item ${selected ? 'selected' : ''}`}
      onClick={onClick}
      data-testid={isTeam ? `channel-${c.id}` : `contact-${c.id}`}
    >
      <div className={`avatar ${isTeam ? 'team' : ''}`}>
        {isTeam ? <Hash size={20} /> : c.picture ? <img src={c.picture} alt="" /> : initials(c.name)}
        {!isTeam && <span className={`presence ${c.online ? 'online' : ''}`} data-testid={`presence-${c.id}`} />}
      </div>
      <div className="contact-main">
        <div className="contact-row">
          <span className="contact-name">
            <span className="contact-name-text">{c.name}</span>
            {isTeam && <span className="contact-sub" data-testid={`channel-online-${c.id}`}>· {c.online_count} of {c.member_count} online</span>}
          </span>
          <span className="contact-time">{formatTime(c.last_message_at)}</span>
        </div>
        <div className="contact-row">
          <span className={`contact-preview ${unread ? 'unread' : ''}`}>
            {c.last_message_at && <PreviewIcon type={c.last_message_type} />}
            {preview(c)}
          </span>
          <span className="contact-badges">
            {c.muted && <VolumeX size={13} className="muted-icon" data-testid={`channel-muted-${c.id}`} />}
            {unread && (
              <span className="unread-badge" data-testid={isTeam ? `channel-unread-${c.id}` : `contact-unread-${c.id}`}>
                {c.unread_count > 99 ? '99+' : c.unread_count}
              </span>
            )}
          </span>
        </div>
      </div>
    </button>
  );
};

export const ConversationList = ({
  conversations, loading, selectedKey, onSelect, searchQuery, onSearchChange, searchFilters, onSearchFiltersChange, userId, onOpenResult,
}) => {
  const channels = conversations.filter((c) => c.kind === 'team');
  const people = conversations.filter((c) => c.kind === 'direct');
  const online = people.filter((c) => c.online).length;
  const searching = isSearchActive(searchQuery, searchFilters);

  return (
    <aside className="comms-panel comms-contacts" data-testid="contacts-panel">
      <div className="contacts-head">
        <span>Conversations</span>
        <span className="contacts-online" data-testid="online-count">{online} online</span>
      </div>
      <MessageSearch
        query={searchQuery}
        onQueryChange={onSearchChange}
        filters={searchFilters}
        onFiltersChange={onSearchFiltersChange}
        conversations={conversations}
        userId={userId}
        onOpenResult={onOpenResult}
      />
      <div className="contacts-list" style={searching ? { display: 'none' } : undefined}>
        {loading && conversations.length === 0 && <div className="contacts-empty">Loading…</div>}
        {!loading && conversations.length === 0 && (
          <div className="contacts-empty" data-testid="contacts-empty">
            No teams yet. Create or join a team to start messaging.
          </div>
        )}
        {channels.length > 0 && <div className="list-section" data-testid="channels-section">Channels</div>}
        {channels.map((c) => (
          <ConversationItem key={c.key} conversation={c} selected={c.key === selectedKey} onClick={() => onSelect(c.key)} />
        ))}
        {people.length > 0 && <div className="list-section" data-testid="people-section">People</div>}
        {people.map((c) => (
          <ConversationItem key={c.key} conversation={c} selected={c.key === selectedKey} onClick={() => onSelect(c.key)} />
        ))}
      </div>
    </aside>
  );
};
